import { Transition } from '@headlessui/react'
import { useContext, useEffect, useRef, useState } from "react";
import { deleteData, OAuthError, patchData, postData, putData } from "../backend";
import { WorkflowContext, useModal } from "../contexts";
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useUser from "../hooks/useUser";
import OAuthModal from './OAuthModal';
import OAuthModalMicrosoft from './OAuthModalMicrosoft';
import WorkflowSelectModal from './WorkflowSelectModal';
import TransitionWorkflowPage from './transitions/TransitionWorkflowPage';
import WorkflowSignatureRequestSidebar from './WorkflowSignatureRequestSidebar';
import EmailEditor, { AttachmentAction, Ref as EmailEdtorRef } from './EmailEditor';
import SignatureRequestEmailRecipients from './SignatureRequestEmailRecipients';
import SignatureRequestEmailContant from './SignatureRequestEmailContant';
import { AbstractEmail, Attachment, SignatureConfig } from '../interfaces';

function WorkflowSignatureRequest() {
  const { hasPermission } = useUser();
  const { t } = useTranslation();
  const { url } = useRouteMatch();

  const history = useHistory();
  const { pathname } = useLocation();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow } = workflowContext;

  const [signatureConfig, setSignatureConfig] = useState<SignatureConfig>();
  const [hasCheckedScopes, setHasCheckedScopes] = useState(false);

  const { openModal } = useModal();

  const emailEditorRef = useRef<EmailEdtorRef>(null);

  useEffect(() => {
    if (workflow.signatureConfig !== null)
      setSignatureConfig(workflow.signatureConfig);
    else
      setSignatureConfig(undefined);
  }, [workflow.signatureConfig]);

  useEffect(() => {
    if (hasCheckedScopes || !pathname.endsWith("/signature-request"))
      return;

    postData("/auth/scopes/", { scopes: ["https://www.googleapis.com/auth/gmail.send"] })
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModal title={t('Portant requires your permission to send emails')} oauthUrl={error.oauthUrl} />);
      })
      .finally(() => setHasCheckedScopes(true));
  }, [hasCheckedScopes, openModal, pathname, t]);

  async function updateSignatureConfig(updates: any) {
    if (!signatureConfig)
      return;

    const updatedSignatureConfig = { ...signatureConfig!, ...updates };
    setSignatureConfig(updatedSignatureConfig);

    await putData(`/signatures/signature-configs/${updatedSignatureConfig.id}/`, updatedSignatureConfig)
      .catch(console.error);

    mutateWorkflow();
  }

  async function updateAttachment(action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) {
    if (action === "CREATE")
      return postData(`/signatures/signature-configs/${signatureConfig!.id}/attachments/`, attachment);

    else if (action === "DELETE")
      return deleteData(`/signatures/signature-config-attachments/${attachmentId}/`);

    else if (action === "UPDATE")
      return patchData(`/signatures/signature-config-attachments/${attachmentId}/`, { sharingMode: attachment!.sharingMode });
  }

  async function checkCanChangeEmailType(emailType: "GMAIL" | "OUTLOOK"): Promise<boolean> {
    if (emailType === "OUTLOOK") {
      try {
        await postData("/microsoft/access-token/");
      }
      catch (error) {
        if (error instanceof OAuthError)
          openModal(<OAuthModalMicrosoft oauthUrl={error.oauthUrl} />);

        return false;
      }
    }

    if (emailType === "GMAIL") {
      try {
        await postData("/auth/scopes/", { scopes: ["https://www.googleapis.com/auth/gmail.send"] });
      }
      catch (error) {
        if (error instanceof OAuthError)
          openModal(<OAuthModal title={t('Portant requires your permission to send emails')} oauthUrl={error.oauthUrl} />);

        return false;
      }
    }

    return true;
  }

  async function updateEmail(email: AbstractEmail) {
    const updatedEmail = await putData(`/signatures/signature-configs/${signatureConfig!.id}/`, email);

    setSignatureConfig(updatedEmail);
    mutateWorkflow({ ...workflow, signatureConfig: updatedEmail });

    return updatedEmail;
  }

  if (!signatureConfig) {
    if (!pathname.endsWith("/signature-request"))
      return null;

    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full w-full">
        <div className="font-semibold font-gilroy text-gray-600 text-center w-96">
          {t("This workflow does not contain a signature request, would you like to add one?")}
        </div>
        <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Add Workflow Signature Request")} subtitle={t("Add a signature request to your workflow")} showSignatureRequest workflowContext={workflowContext} />)}>
          {t("Add Signature Request")}
        </button>
      </div>
    );
  }

  return (
    <>
      <TransitionWorkflowPage show={pathname.endsWith("/signature-request")}>

        {hasPermission(workflow.team, "workflows.edit") &&
          <WorkflowSignatureRequestSidebar setSignatureConfig={setSignatureConfig} signatureConfig={signatureConfig} updateSignatureConfig={updateSignatureConfig} />
        }

        <Transition.Child className="flex flex-col items-center justify-center m-auto"
          enter="transition-opacity duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <EmailEditor
            ref={emailEditorRef}
            initialEmail={signatureConfig}
            updateEmail={updateEmail}
            updateAttachment={updateAttachment}
            checkCanChangeEmailType={checkCanChangeEmailType}
            onClose={() => history.push(url)}
            customTo={<SignatureRequestEmailRecipients signatureConfig={signatureConfig} />}
            customBodyComponent={SignatureRequestEmailContant}
            disabled={!hasPermission(workflow.team, "workflows.edit")}
            title={t("Request Signatures")}
          />
        </Transition.Child>
      </TransitionWorkflowPage>
    </>
  );

}

export default WorkflowSignatureRequest;
